import { useState, useMemo, useEffect, useCallback } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import logoImage from "@assets/Chief_in_Tech_Global_Logo.png";
import { optimizedImageUrl } from "@/lib/optimized-image";
import { 
  Menu, 
  LogIn, 
  LogOut, 
  User, 
  Settings, 
  ChevronDown,
  Home,
  Newspaper,
  Users,
  Trophy,
  Calendar,
  Briefcase,
  LayoutGrid,
  Rocket,
  GraduationCap,
  CircleDot,
  Lightbulb,
  Building2,
  HandshakeIcon,
  TrendingUp,
  FileText,
  Mic,
  Shield,
  Bot,
  BarChart3,
  UserCog,
  BookOpen,
  Brain,
  Target,
  GitBranch,
  Workflow,
  Mail,
  Activity,
  Eye,
  Coins,
  Award,
  Globe,
  Star,
  MapPin,
  Ticket,
  Radio,
  ShoppingBag,
  Video,
  Sparkles,
  Heart,
  MessageSquare,
  MoreHorizontal,
  Moon,
  Sun,
  type LucideIcon
} from "lucide-react";

const iconMap: Record<string, LucideIcon> = {
  Menu, Newspaper, Users, Trophy, Calendar, Briefcase, LayoutGrid, Rocket,
  GraduationCap, CircleDot, Lightbulb, Building2, HandshakeIcon, TrendingUp,
  FileText, Mic, Shield, Bot, BarChart3, UserCog, BookOpen, Brain, Target,
  GitBranch, Workflow, Mail, Activity, Eye, Coins, Award, Settings, User,
  Globe, Star, MapPin, Ticket, Radio, ShoppingBag, Video, Sparkles
};
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { useTheme } from "@/components/theme-provider";
import { Search } from "@/components/Search";
import { NotificationBell } from "@/components/NotificationBell";
import { useRoleImpersonation, type ImpersonatedRole } from "@/components/RoleSwitcher";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import { getInitials, getDisplayName } from "@/lib/authUtils";
import { FullNavPanel } from "@/components/navigation/FullNavPanel";
import { SectionSubNav } from "@/components/navigation/SectionSubNav";
import { sections, moreMenuItems, getActiveSection, getActiveSubItem, getLevel3Items, getNavigationDepth, getSubNavItems, type Section } from "@/lib/navigationConfig";
import { getActiveSubdomain, getSubdomainSection, getMainDomainUrl, needsCrossDomainNav, getCrossDomainHref } from "@/lib/domainUtils";
import type { User as UserType, ProductBrand, NavigationMenuItem } from "@shared/schema";
import { cn } from "@/lib/utils";
import { getInjectedBrandLogos } from "@/lib/brandLogos";
import { isCapitalDomain } from "@/lib/fundLinks";
import { isChiefInAIDomain } from "@/lib/domainUtils";
import { ChevronRight } from "lucide-react";

type ProductLine = "capital" | "media" | "network" | "summit" | "book" | "chiefinai";

type ConvPreview = {
  id: number;
  contextType: string;
  title: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  unreadCount: number;
  participants: { userId: string; firstName: string | null; lastName: string | null; profileImageUrl: string | null }[];
};

function formatTimeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function MessagingBadge() {
  const [open, setOpen] = useState(false);
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: ["/api/messaging/unread-count"],
    refetchInterval: 30000,
  });
  const count = unreadData?.count || 0;

  const { data: conversations = [] } = useQuery<ConvPreview[]>({
    queryKey: ["/api/messaging/conversations"],
    enabled: open,
    staleTime: 10000,
  });

  const recentConversations = conversations.slice(0, 5);

  const getConvDisplayName = useCallback((conv: ConvPreview) => {
    if (conv.title) {
      // Shorten cohort channel titles: strip "CohortName — " or "C5 · " prefix
      let shortTitle = conv.title;
      if (shortTitle.includes(' — ')) {
        shortTitle = shortTitle.split(' — ').pop() || shortTitle;
      } else if (/^C\d+ · /.test(shortTitle)) {
        shortTitle = shortTitle.replace(/^C\d+ · /, '');
      }
      return shortTitle;
    }
    const others = conv.participants.filter(p => p.userId !== user?.id);
    if (others.length === 0) return "Conversation";
    return others.map(p => [p.firstName, p.lastName].filter(Boolean).join(" ") || "User").join(", ");
  }, [user?.id]);

  const getConvAvatar = useCallback((conv: ConvPreview) => {
    const others = conv.participants.filter(p => p.userId !== user?.id);
    if (others.length > 0) return others[0];
    return conv.participants[0] || null;
  }, [user?.id]);

  const handleConvClick = useCallback((convId: number) => {
    setOpen(false);
    setLocation(`/messages?conv=${convId}`);
  }, [setLocation]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="button-messages">
          <MessageSquare className="h-5 w-5" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] font-medium flex items-center justify-center px-1" data-testid="badge-unread-messages">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" data-testid="popover-messages">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h4 className="text-sm font-semibold">Messages</h4>
          {count > 0 && (
            <span className="text-xs text-primary font-medium">{count} unread</span>
          )}
        </div>
        <div className="max-h-[320px] overflow-y-auto">
          {recentConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <MessageSquare className="w-8 h-8 text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">No conversations yet</p>
            </div>
          ) : (
            recentConversations.map(conv => {
              const avatarUser = getConvAvatar(conv);
              const initials = avatarUser
                ? `${avatarUser.firstName?.[0] || ""}${avatarUser.lastName?.[0] || ""}`
                : "?";
              return (
                <button
                  key={conv.id}
                  onClick={() => handleConvClick(conv.id)}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors border-b last:border-b-0 ${
                    conv.unreadCount > 0 ? "bg-primary/5" : ""
                  }`}
                  data-testid={`message-preview-${conv.id}`}
                >
                  <Avatar className="w-9 h-9 flex-shrink-0 mt-0.5">
                    <AvatarImage src={avatarUser?.profileImageUrl || undefined} />
                    <AvatarFallback className="text-[11px]">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm truncate ${conv.unreadCount > 0 ? "font-semibold" : "font-medium"}`}>
                        {getConvDisplayName(conv)}
                      </span>
                      <span className="text-[10px] text-muted-foreground flex-shrink-0">
                        {formatTimeAgo(conv.lastMessageAt)}
                      </span>
                    </div>
                    <p className={`text-xs truncate mt-0.5 ${conv.unreadCount > 0 ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                      {conv.lastMessagePreview || "No messages yet"}
                    </p>
                  </div>
                  {conv.unreadCount > 0 && (
                    <span className="min-w-[18px] h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] font-medium flex items-center justify-center px-1 flex-shrink-0 mt-1">
                      {conv.unreadCount}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
        <div className="border-t px-4 py-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-primary hover:text-primary"
            onClick={() => { setOpen(false); setLocation("/messages"); }}
            data-testid="button-view-all-messages"
          >
            View all messages
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface HeaderProps {
  variant?: "full" | "simplified";
  showSearch?: boolean;
  showNotifications?: boolean;
  showThemeToggle?: boolean;
  showUserMenu?: boolean;
  sticky?: boolean;
}

const sectionKeys: Section[] = ["discover", "network", "capital", "programs", "events", "membership", "summit"];

export function Header({
  variant = "full",
  showSearch = true,
  showNotifications = true,
  showThemeToggle = true,
  showUserMenu = true,
  sticky = true,
}: HeaderProps = {}) {
  const { data: productBrands = [] } = useQuery<ProductBrand[]>({
    queryKey: ["/api/brands"],
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });

  const LOGO_CACHE_KEY = "chiefintech_brand_logos";

  const getCachedLogos = (): Record<string, string> => {
    const injected = getInjectedBrandLogos();
    try {
      const cached = localStorage.getItem(LOGO_CACHE_KEY);
      const parsed = cached ? JSON.parse(cached) : {};
      // Server-injected logos take precedence over stale localStorage so an
      // admin update is reflected on the very first paint.
      return { ...parsed, ...injected };
    } catch {
      return { ...injected };
    }
  };

  const [cachedLogos, setCachedLogos] = useState<Record<string, string>>(getCachedLogos);
  const [navPanelOpen, setNavPanelOpen] = useState(false);

  useEffect(() => {
    if (productBrands.length > 0) {
      const logoMap: Record<string, string> = {};
      productBrands.forEach(brand => {
        const logo = brand.logoUrl || brand.darkLogoUrl || brand.iconUrl;
        if (logo) {
          logoMap[brand.productKey] = logo;
        }
        [brand.logoUrl, brand.darkLogoUrl, brand.iconUrl].forEach(url => {
          if (url) {
            const img = new Image();
            img.src = url;
          }
        });
      });
      try {
        localStorage.setItem(LOGO_CACHE_KEY, JSON.stringify(logoMap));
      } catch {
      }
      setCachedLogos(logoMap);
    }
  }, [productBrands]);

  const [location] = useLocation();
  const { user, isAuthenticated, isLoading, hasRole, activeRoles } = useAuth();
  const { impersonatedRole, setImpersonatedRole, isImpersonating } = useRoleImpersonation();
  const { setTheme, resolvedTheme } = useTheme();

  const activeSubdomain = useMemo(() => getActiveSubdomain(), []);
  const subdomainSection = useMemo(() => getSubdomainSection(), []);

  const activeSection = getActiveSection(location);
  const navDepth = getNavigationDepth(location, activeSection);
  const activeSubItem = activeSection ? getActiveSubItem(location, activeSection) : null;

  // Check if user is in an active cohort
  const { data: myCohorts } = useQuery<{ cohortId: number; status: string; cohort: { name: string; status: string } }[]>({
    queryKey: ["/api/cohorts/my"],
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
  const activeCohort = myCohorts?.find(c => ["enrolled", "active"].includes(c.status) && ["active", "enrolling"].includes(c.cohort.status));

  const getActivePillar = (): ProductLine | null => {
    if (isCapitalDomain()) return "capital";
    if (isChiefInAIDomain()) return "chiefinai";
    const path = location.split("?")[0];
    // "/100" is the chiefinai.com public path for the Chief in AI 100; without it
    // the same page would show the Chief in Tech logo on /100 and the Chief in AI
    // one on /chief-in-ai-100.
    if (path.startsWith("/chief-in-ai") || path === "/100" || path.startsWith("/100/")) return "chiefinai";
    if (path.startsWith("/media") || path.startsWith("/articles") || path.startsWith("/leaders") || path.startsWith("/books") ||
        (path.startsWith("/events") && !path.includes("chief-in-tech-summit"))) return "media";
    if (path.startsWith("/directory") || path.startsWith("/circles") || 
        path.startsWith("/jobs") || path.startsWith("/executive-search") || 
        path.startsWith("/board-match") || path.startsWith("/network") ||
        path.startsWith("/membership") || path.startsWith("/profile")) return "network";
    if (path.startsWith("/capital") || path.startsWith("/funding") || path.startsWith("/dealroom") || path.startsWith("/investor-network")) return "capital";
    if (path.startsWith("/summit") || path.includes("/events/chief-in-tech-summit")) return "summit";
    if (path.startsWith("/book") && !path.startsWith("/books")) return "book";
    return null;
  };

  const activePillar = getActivePillar();

  const getBrandLogo = (brand: typeof productBrands[0] | undefined): string | null => {
    if (!brand) return null;
    return brand.logoUrl || brand.darkLogoUrl || brand.iconUrl || null;
  };

  const getDynamicLogo = () => {
    // Chief in AI domain: always use the dedicated logo
    if (isChiefInAIDomain() || activePillar === "chiefinai") {
      return "/uploads/brands/Chief in AI.png";
    }
    const brandKey = activePillar || "global";
    const cachedLogo = cachedLogos[brandKey];
    if (cachedLogo) return cachedLogo;
    const cachedGlobalLogo = cachedLogos["global"];
    if (productBrands.length > 0) {
      const pillarBrand = productBrands.find(b => b.productKey === brandKey);
      const pillarLogo = getBrandLogo(pillarBrand);
      if (pillarLogo) return pillarLogo;
      const globalBrand = productBrands.find(b => b.productKey === "global");
      const globalLogo = getBrandLogo(globalBrand);
      if (globalLogo) return globalLogo;
    }
    return cachedGlobalLogo || logoImage;
  };

  const dynamicLogoSrc = getDynamicLogo();

  const getLoginHref = () => {
    const currentPath = window.location.pathname + window.location.search;
    const excludedPaths = ["/", "/login", "/register", "/forgot-password"];
    const shouldRedirect = !excludedPaths.includes(window.location.pathname);
    if (shouldRedirect && currentPath !== "/") {
      return `/login?redirect=${encodeURIComponent(currentPath)}`;
    }
    return "/login";
  };

  useEffect(() => {
    if (navPanelOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [navPanelOpen]);

  return (
    <>
      <header
        className={`${sticky ? "sticky top-0" : ""} z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60`}
        data-testid="header"
      >
        {isImpersonating && impersonatedRole && (
          <div 
            className="bg-primary text-primary-foreground py-1 px-4 text-center text-sm flex items-center justify-center gap-4"
            data-testid="banner-impersonation"
          >
            <span data-testid="text-impersonated-role">
              Viewing as <strong className="capitalize">{impersonatedRole}</strong> role
            </span>
            <Button 
              variant="secondary" 
              size="sm"
              onClick={() => setImpersonatedRole(null)}
              data-testid="button-exit-impersonation"
            >
              Exit
            </Button>
          </div>
        )}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 min-w-0 items-center gap-4">
            <div className="flex min-w-0 items-center gap-4">
              <Link href={activeSubdomain ? (sections[subdomainSection as Section]?.href || "/") : isChiefInAIDomain() ? "/chief-in-ai" : (user?.isAdmin ? "/?front=true" : "/")} data-testid="link-logo" className="flex h-9 min-w-[120px] shrink-0 items-center">
                <img 
                  src={optimizedImageUrl(dynamicLogoSrc, 280)} 
                  alt={activePillar === "chiefinai" ? "Chief in AI" : activePillar ? `Chief in Tech ${activePillar.charAt(0).toUpperCase() + activePillar.slice(1)}` : "Chief in Tech"} 
                  width={140}
                  height={36}
                  className="h-9 min-w-[120px] max-w-[180px] w-[140px] object-contain dark:invert dark:hue-rotate-180 dark:brightness-200 dark:drop-shadow-[0_0_1px_rgba(255,255,255,0.5)]"
                />
              </Link>

              {variant === "full" && navDepth === 0 && isChiefInAIDomain() && (
                <nav className="hidden lg:flex items-center gap-0.5" aria-label="Chief in AI navigation" data-testid="nav-chiefinai">
                  <ul className="flex items-center gap-0.5">
                    <li>
                      <Link
                        href="/events/salon/chief-in-ai-salon-davos-2027"
                        className={cn(
                          "flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors",
                          location.includes("/salon")
                            ? "bg-secondary text-secondary-foreground"
                            : "text-foreground/80 hover:text-foreground hover:bg-muted/50"
                        )}
                        data-testid="nav-ai-salon"
                      >
                        Salon
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/100"
                        className={cn(
                          "flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors",
                          location === "/100" || location.startsWith("/100/") || location.startsWith("/chief-in-ai-100")
                            ? "bg-secondary text-secondary-foreground"
                            : "text-foreground/80 hover:text-foreground hover:bg-muted/50"
                        )}
                        data-testid="nav-ai-100"
                      >
                        The 100
                      </Link>
                    </li>
                    {/* "100 Council" nav item removed 2026-08-09: the published
                        Chief in AI 100 (The 100, above) superseded the pre-launch
                        council/nominations page, which now redirects to the list. */}
                    <li>
                      <a
                        href="https://www.chiefintech.com/join"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium rounded-md text-foreground/80 hover:text-foreground hover:bg-muted/50 transition-colors"
                        data-testid="nav-ai-join"
                      >
                        Join
                      </a>
                    </li>
                  </ul>
                </nav>
              )}

              {variant === "full" && navDepth === 0 && !isChiefInAIDomain() && (
                <nav className="hidden items-center gap-0.5 xl:flex" aria-label="Main navigation" data-testid="nav-desktop">
                  <ul className="flex items-center gap-0.5">
                    <li>
                      <Link
                        href="/"
                        className={cn(
                          "flex items-center gap-1 px-2 py-1.5 text-sm font-medium rounded-md transition-colors",
                          location === "/"
                            ? "bg-secondary text-secondary-foreground"
                            : "text-foreground/80 hover:text-foreground hover:bg-muted/50"
                        )}
                        data-testid="nav-home"
                        title="Home"
                      >
                        <Home className="h-4 w-4" />
                      </Link>
                    </li>

                    {user?.isAdmin && (
                      <li>
                        <Link
                          href="/admin"
                          className={cn(
                            "flex items-center gap-1 px-2 py-1.5 text-sm font-medium rounded-md transition-colors",
                            location.startsWith("/admin")
                              ? "bg-secondary text-secondary-foreground"
                              : "text-foreground/80 hover:text-foreground hover:bg-muted/50"
                          )}
                          data-testid="nav-admin"
                          title="Admin Dashboard"
                        >
                          <Shield className="h-4 w-4" />
                        </Link>
                      </li>
                    )}

                    {sectionKeys.map((key) => {
                      const config = sections[key];
                      const isActive = activeSection === key;
                      const crossDomain = needsCrossDomainNav(key);
                      const navClassName = cn(
                        "flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors",
                        isActive
                          ? "bg-secondary text-secondary-foreground"
                          : "text-foreground/80 hover:text-foreground hover:bg-muted/50"
                      );
                      return (
                        <li key={key}>
                          {crossDomain ? (
                            <a
                              href={getCrossDomainHref(key, config.href)}
                              className={navClassName}
                              data-testid={`nav-${key}`}
                            >
                              {config.label}
                            </a>
                          ) : (
                            <Link
                              href={config.href}
                              className={navClassName}
                              data-testid={`nav-${key}`}
                            >
                              {config.label}
                            </Link>
                          )}
                        </li>
                      );
                    })}

                    <li>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="flex items-center gap-1 px-2 py-1.5 text-sm font-medium rounded-md text-foreground/80 hover:text-foreground hover:bg-muted/50 transition-colors"
                            data-testid="nav-more"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">More</span>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48">
                          {moreMenuItems.map((item) => (
                            <DropdownMenuItem key={item.href} asChild>
                              <Link href={item.href} data-testid={`nav-more-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                                {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                                {item.label}
                              </Link>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </li>
                  </ul>
                </nav>
              )}

              {variant === "full" && navDepth >= 1 && activeSection && (
                <nav className="hidden min-w-0 items-center gap-0.5 overflow-hidden md:flex xl:hidden" aria-label="Mobile breadcrumb" data-testid="nav-breadcrumb-mobile">
                  {activeSubdomain ? (
                    <a href={getMainDomainUrl("/")} className="flex items-center text-xs text-muted-foreground hover:text-foreground shrink-0">
                      <Home className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <Link href="/" className="flex items-center text-xs text-muted-foreground hover:text-foreground shrink-0">
                      <Home className="h-3.5 w-3.5" />
                    </Link>
                  )}
                  <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                  {navDepth === 2 && activeSubItem ? (
                    <>
                      {needsCrossDomainNav(activeSection) ? (
                        <a href={getCrossDomainHref(activeSection, sections[activeSection].href)} className="text-xs text-muted-foreground hover:text-foreground truncate shrink-0">
                          {sections[activeSection].label}
                        </a>
                      ) : (
                        <Link href={sections[activeSection].href} className="text-xs text-muted-foreground hover:text-foreground truncate shrink-0">
                          {sections[activeSection].label}
                        </Link>
                      )}
                      <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                      <span className="text-xs font-medium text-foreground truncate">
                        {activeSubItem.label}
                      </span>
                    </>
                  ) : (
                    <span className="text-xs font-medium text-foreground truncate">
                      {sections[activeSection].label}
                    </span>
                  )}
                </nav>
              )}

              {variant === "full" && navDepth >= 1 && activeSection && (
                <nav className="hidden items-center gap-1 xl:flex" aria-label="Breadcrumb navigation" data-testid="nav-breadcrumb">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex items-center gap-1 px-2 py-1.5 text-sm font-medium rounded-md text-foreground/80 hover:text-foreground hover:bg-muted/50 transition-colors"
                        data-testid="breadcrumb-home"
                      >
                        <Home className="h-4 w-4" />
                        <span>Home</span>
                        <ChevronDown className="h-3 w-3 opacity-60" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      <DropdownMenuItem asChild>
                        {activeSubdomain ? (
                          <a href={getMainDomainUrl("/")} data-testid="breadcrumb-home-link">
                            <Home className="mr-2 h-4 w-4" />
                            Home
                          </a>
                        ) : (
                          <Link href="/" data-testid="breadcrumb-home-link">
                            <Home className="mr-2 h-4 w-4" />
                            Home
                          </Link>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {sectionKeys.map((key) => {
                        const config = sections[key];
                        const crossDomain = needsCrossDomainNav(key);
                        return (
                          <DropdownMenuItem key={key} asChild>
                            {crossDomain ? (
                              <a href={getCrossDomainHref(key, config.href)} data-testid={`breadcrumb-section-${key}`}>
                                <config.icon className="mr-2 h-4 w-4" />
                                {config.label}
                              </a>
                            ) : (
                              <Link href={config.href} data-testid={`breadcrumb-section-${key}`}>
                                <config.icon className="mr-2 h-4 w-4" />
                                {config.label}
                              </Link>
                            )}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />

                  {navDepth === 1 && (
                    <Link
                      href={sections[activeSection].href}
                      className="flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium rounded-md bg-secondary text-secondary-foreground"
                      data-testid="breadcrumb-section-current"
                    >
                      {(() => { const Icon = sections[activeSection].icon; return <Icon className="h-4 w-4" />; })()}
                      {sections[activeSection].label}
                    </Link>
                  )}

                  {navDepth === 2 && (
                    <>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium rounded-md text-foreground/80 hover:text-foreground hover:bg-muted/50 transition-colors"
                            data-testid="breadcrumb-section-dropdown"
                          >
                            {(() => { const Icon = sections[activeSection].icon; return <Icon className="h-4 w-4" />; })()}
                            {sections[activeSection].label}
                            <ChevronDown className="h-3 w-3 opacity-60" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-52">
                          {getSubNavItems(activeSection, user?.role, activeRoles).map((item) => (
                            <DropdownMenuItem key={item.href} asChild>
                              <Link href={item.href} data-testid={`breadcrumb-l2-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                                {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                                {item.label}
                              </Link>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />

                      {activeSubItem && (
                        <Link
                          href={activeSubItem.href}
                          className="flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium rounded-md bg-secondary text-secondary-foreground"
                          data-testid="breadcrumb-subitem-current"
                        >
                          {activeSubItem.icon && (() => { const Icon = activeSubItem.icon; return <Icon className="h-4 w-4" />; })()}
                          {activeSubItem.label}
                        </Link>
                      )}
                    </>
                  )}
                </nav>
              )}
            </div>

            {showSearch && (
              <div className="hidden md:flex flex-1 max-w-md mx-4">
                <Search expanded />
              </div>
            )}

            <div className="flex items-center gap-1.5 shrink-0 ml-auto">
              {showSearch && (
                <div className="md:hidden">
                  <Search />
                </div>
              )}
              {isAuthenticated && <MessagingBadge />}
              {showNotifications && isAuthenticated && <NotificationBell />}
              {showThemeToggle && <div className="hidden sm:block"><ThemeToggle /></div>}

              {showUserMenu && (
                isLoading ? (
                  <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
                ) : isAuthenticated && user ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center gap-1.5 px-1.5"
                        data-testid="button-user-menu"
                      >
                        <Avatar className="h-7 w-7">
                          <AvatarImage
                            src={user.profileImageUrl || undefined}
                            alt={getDisplayName(user.firstName, user.lastName, user.email)}
                            className="object-cover"
                          />
                          <AvatarFallback className="text-[10px]">
                            {getInitials(user.firstName, user.lastName)}
                          </AvatarFallback>
                        </Avatar>
                        <ChevronDown className="h-3.5 w-3.5 hidden sm:block" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel data-testid="text-user-name">
                        {getDisplayName(user.firstName, user.lastName, user.email)}
                        {user.email && (
                          <p className="text-xs font-normal text-muted-foreground truncate">
                            {user.email}
                          </p>
                        )}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link href="/home" data-testid="link-dashboard">
                          <LayoutGrid className="mr-2 h-4 w-4" />
                          Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/profile" data-testid="link-profile">
                          <User className="mr-2 h-4 w-4" />
                          Profile
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link href="/account" data-testid="link-account">
                          <Settings className="mr-2 h-4 w-4" />
                          Account
                        </Link>
                      </DropdownMenuItem>
                      {activeCohort && (
                        <DropdownMenuItem asChild>
                          <Link href={`/cohort/${activeCohort.cohortId}`} data-testid="link-my-cohort">
                            <GraduationCap className="mr-2 h-4 w-4" />
                            My Cohort
                          </Link>
                        </DropdownMenuItem>
                      )}
                      
                      {user.isAdmin && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link href="/admin" data-testid="link-admin">
                              <Shield className="mr-2 h-4 w-4" />
                              Admin Dashboard
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href="/?front=true" data-testid="link-preview-landing">
                              <Eye className="mr-2 h-4 w-4" />
                              Preview Landing Page
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger data-testid="button-impersonate-role">
                              <UserCog className="mr-2 h-4 w-4" />
                              {impersonatedRole ? (
                                <span className="flex items-center gap-2">
                                  View as: {impersonatedRole.charAt(0).toUpperCase() + impersonatedRole.slice(1)}
                                  <Badge variant="secondary" className="text-[10px] px-1 py-0">Active</Badge>
                                </span>
                              ) : (
                                "Impersonate Role"
                              )}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="w-48" data-testid="menu-impersonate-roles">
                              {([
                                { id: "explorer", label: "Explorer", icon: BookOpen },
                                { id: "founder", label: "Founder", icon: Rocket },
                                { id: "investor", label: "Investor", icon: TrendingUp },
                                { id: "advisor", label: "Advisor", icon: User },
                                { id: "contributor", label: "Contributor", icon: FileText },
                                { id: "executive", label: "Executive", icon: Briefcase },
                              ] as const).map((role) => (
                                <DropdownMenuItem
                                  key={role.id}
                                  onClick={() => setImpersonatedRole(role.id as ImpersonatedRole)}
                                  data-testid={`menu-item-role-${role.id}`}
                                >
                                  <role.icon className="mr-2 h-4 w-4" />
                                  {role.label}
                                  {impersonatedRole === role.id && (
                                    <Badge variant="default" className="ml-auto text-[10px] px-1 py-0">Current</Badge>
                                  )}
                                </DropdownMenuItem>
                              ))}
                              {impersonatedRole && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => setImpersonatedRole(null)}
                                    className="text-destructive"
                                    data-testid="menu-item-exit-impersonation"
                                  >
                                    Exit Impersonation
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        </>
                      )}
                      {hasRole("founder") && (
                        <DropdownMenuItem asChild>
                          <Link href="/capital/portfolio-hub" data-testid="link-founder-workspace">
                            <Rocket className="mr-2 h-4 w-4" />
                            Founder Workspace
                          </Link>
                        </DropdownMenuItem>
                      )}
                      {hasRole("investor") && (
                        <DropdownMenuItem asChild>
                          <Link href="/capital/dealroom" data-testid="link-investor-dealroom">
                            <Briefcase className="mr-2 h-4 w-4" />
                            Investor Dealroom
                          </Link>
                        </DropdownMenuItem>
                      )}
                      {user.contributorTier && user.contributorTier !== "reader" && (
                        <DropdownMenuItem asChild>
                          <Link href="/contributor" data-testid="link-contributor">
                            <FileText className="mr-2 h-4 w-4" />
                            Contributor Hub
                          </Link>
                        </DropdownMenuItem>
                      )}
                      
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link href={user.isPremium || user.platformMembershipTier ? "/account#billing" : "/membership"} data-testid="link-pricing">
                          <TrendingUp className="mr-2 h-4 w-4" />
                          {user.isPremium || user.platformMembershipTier ? "Manage Subscription" : "Upgrade to Premium"}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger data-testid="button-theme-menu">
                          {resolvedTheme === "dark" ? <Moon className="mr-2 h-4 w-4" /> : <Sun className="mr-2 h-4 w-4" />}
                          Theme
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="w-36" data-testid="menu-theme-options">
                          <DropdownMenuItem onClick={() => setTheme("light")} data-testid="menu-item-user-theme-light">
                            <Sun className="mr-2 h-4 w-4" /> Light
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setTheme("dark")} data-testid="menu-item-user-theme-dark">
                            <Moon className="mr-2 h-4 w-4" /> Dark
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setTheme("system")} data-testid="menu-item-user-theme-system">
                            <Settings className="mr-2 h-4 w-4" /> System
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={() => {
                          void apiRequest("POST", "/api/logout")
                            .finally(() => { window.location.href = "/"; });
                        }}
                        data-testid="button-logout"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Log out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <>
                    <Button asChild size="icon" variant="ghost" className="sm:hidden" data-testid="button-login-mobile">
                      <Link href={getLoginHref()}>
                        <LogIn className="h-5 w-5" />
                        <span className="sr-only">Log in</span>
                      </Link>
                    </Button>
                    <Button asChild size="sm" className="hidden sm:inline-flex" data-testid="button-login">
                      <Link href={getLoginHref()}>
                        <LogIn className="mr-1.5 h-4 w-4" />
                        Log in
                      </Link>
                    </Button>
                  </>
                )
              )}

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setNavPanelOpen(true)}
                data-testid="button-hamburger"
                className="h-8 w-8"
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">Open full navigation</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {variant === "full" && <SectionSubNav navDepth={navDepth} activeSubItem={activeSubItem} />}

      <FullNavPanel
        open={navPanelOpen}
        onClose={() => setNavPanelOpen(false)}
        isAdmin={!!user?.isAdmin}
      />
    </>
  );
}
